import React, { FC, useMemo, useEffect } from 'react'
import { useTranslation } from 'next-i18next'
import {
    UseFormRegister,
    useWatch,
    Control,
    UseFormUnregister,
    FieldErrors,
    FieldPath,
    ErrorOption
} from 'react-hook-form'
import { GiftCardProduct, PriceRange } from '@pylot-data/pylotschema'
import type { GiftCardProductInputArgs } from './GiftCardProduct'
import {
    SET_MESSAGE,
    SET_TARGET
} from '@corratech/pylot-product-giftcard/src/reducer'
import { formatPrice } from '@corratech/pylot-price'
import { useRouter } from 'next/router'
import cn from 'classnames'

type SelectedAmountAction =
    | { type: 'SET_MESSAGE'; payload: string }
    | { type: 'SET_TARGET'; payload: string }

export type Props = {
    register: UseFormRegister<GiftCardProductInputArgs>
    product: GiftCardProduct
    dispatch: React.Dispatch<SelectedAmountAction>
    control: Control<GiftCardProductInputArgs>
    unregister: UseFormUnregister<GiftCardProductInputArgs>
    price_range: PriceRange
    errors: FieldErrors<GiftCardProductInputArgs>
    clearErrors: (
        name?:
            | FieldPath<GiftCardProductInputArgs>
            | FieldPath<GiftCardProductInputArgs>[]
    ) => void
    setError: (
        name: FieldPath<GiftCardProductInputArgs>,
        error: ErrorOption,
        options?: { shouldFocus: boolean }
    ) => void
}

export enum OtherAmountEnum {
    Other = 'other'
}

export const AmountDropdown: FC<Props> = ({
    register,
    product,
    dispatch,
    control,
    unregister,
    price_range,
    errors,
    clearErrors,
    setError
}) => {
    const { t } = useTranslation(['pdp'])
    const { locale = 'en' } = useRouter()
    const { allow_open_amount, giftcard_amounts } = product

    useEffect(() => {
        dispatch({
            type: SET_MESSAGE,
            payload: t(`From {{min}} to {{max}}`, {
                min: formatPrice(price_range.minimum_price.final_price, locale),
                max: formatPrice(price_range.maximum_price!.final_price, locale)
            })
        })
    }, [])
    const amount = useWatch({
        control,
        name: 'gift_amounts'
    })

    useEffect(() => {
        if (amount !== OtherAmountEnum.Other) {
            unregister('gift_open_amounts')
        }
    }, [amount])

    const amountData = useMemo(() => {
        const options: JSX.Element[] = []
        const lookup = new Map<string, number>()
        let minValue = Number.MAX_VALUE
        let maxValue = Number.MIN_VALUE

        giftcard_amounts?.forEach((amount, key) => {
            if (amount?.uid) {
                const value = amount.website_value || 0
                options.push(
                    <option key={amount.uid} value={amount.uid}>
                        {amount.value}
                    </option>
                )
                lookup.set(amount.uid, value)
                if (value < minValue) {
                    minValue = value
                }
                if (value > maxValue) {
                    maxValue = value
                }
            }
        })
        return { options, lookup, minValue, maxValue }
    }, [giftcard_amounts])

    const amountOptions = amountData.options
    const amountLookup = amountData.lookup

    const priceGiftCardDefault = () =>
        dispatch({
            type: SET_MESSAGE,
            payload: t(`pdp||giftCard|From {{min}} to {{max}}`, {
                min: formatPrice(price_range.minimum_price.final_price, locale),
                max: formatPrice(price_range.maximum_price!.final_price, locale)
            })
        })

    const handleAmountChange = (
        event: React.ChangeEvent<HTMLSelectElement>
    ) => {
        if (event.target.value === OtherAmountEnum.Other) {
            clearErrors('gift_amounts')
            priceGiftCardDefault()
            dispatch({
                type: SET_TARGET,
                payload: 'other'
            })
        } else if (!event.target.value) {
            setError('gift_amounts', {
                type: 'required',
                message: ''
            })
            priceGiftCardDefault()
            dispatch({
                type: SET_TARGET,
                payload: 'dropdown'
            })
        } else {
            clearErrors('gift_amounts')
            dispatch({
                type: SET_MESSAGE,
                payload: formatPrice(
                    {
                        ...price_range.maximum_price!.final_price,
                        value: amountLookup.get(event.target.value)
                    },
                    locale
                )
            })
            dispatch({
                type: SET_TARGET,
                payload: 'dropdown'
            })
        }
    }

    if (!giftcard_amounts?.length) return null

    return (
        <div className="form-group">
            <label className="heading">
                {t(`pdp||giftCard|Choose Gift Card Value`)}
                <span className="asterisk">*</span>
            </label>
            {/* eslint-disable-next-line jsx-a11y/no-onchange */}
            <select
                id="gift_amounts"
                className={cn('form-select', {
                    'border-alert-danger': errors.gift_amounts
                })}
                defaultValue=""
                {...register('gift_amounts', { required: true })}
                onChange={handleAmountChange}
            >
                <option value="" disabled>
                    {t('pdp||giftCard|Select an option...')}{' '}
                </option>
                {amountOptions}
                {allow_open_amount && (
                    <option value={OtherAmountEnum.Other}>
                        {t('pdp||giftCard|Select your amount')}
                    </option>
                )}
            </select>
            {errors.gift_amounts && (
                <span className="error">
                    {t('pdp||giftCard|This is required field')}
                </span>
            )}
        </div>
    )
}
